import oracleApi from 'app-lib/apis/oracleApi'
import assetUtils, { Asset } from 'app-lib/assetUtils'
import { asyncRetry } from 'app-lib/asyncUtils'
import { DEX } from 'app-lib/dexSelector/types'
import errorUtils from 'app-lib/errorUtils'
import loanUtils from 'app-lib/loanUtils'
import numberUtils, { formatPrecision } from 'app-lib/numberUtils'
import swapPayloadUtils from 'app-lib/swapPayloadUtils'
import swapRateUtils from 'app-lib/swapRateUtils'
import tradeUtils, { getPairConfig, IPairConfigDetails } from 'app-lib/tradeUtils'
import {
  ChainId,
  getBlockExplorerAddressLink,
  isChainSupported,
} from 'bzx-common/src/config/appConfig'
import { ITradeRequest, PositionType, TradeType } from 'bzx-common/src/domain/trade.types'
import { debounce, DebouncedFunc } from 'lodash'
import * as mobx from 'mobx'
import { BigNumber } from '@0x/utils'
import RootStore from '../RootStore'
import TradeStore from './TradeStore'
import { TransactionStatus } from '../TransactionStore'
import { TransactionType } from 'bzx-common/src/domain/txTypes'

export type NewPositionIssue =
  | 'INVALID_TRADE_AMOUNT'
  | 'LOW_LIQUIDITY'
  | 'NOT_CONNECTED'
  | 'NOT_ENOUGH_FUNDS'
  | 'ZERO_BALANCE'

export interface INewPositionProps {
  balancesError: Error | null
  balancesPending: boolean
  baseToken: Asset
  baseTokenBalance: BigNumber
  baseTokenPrice: BigNumber
  depositToken: Asset
  dex: DEX
  entryPrice: BigNumber
  exposureValue: BigNumber
  inputTradeAmount: string
  interestRate: BigNumber
  isLoading: boolean
  leverage: number
  liquidationPrice: BigNumber
  loanDataBytes: string
  maintenanceMargin: BigNumber
  marketLiquidity: BigNumber
  marketPair: { baseToken: Asset; quoteToken: Asset }
  maxLeverage: number
  maxTradeValue: BigNumber
  maxTradeValuePercent: number
  minInitialMargin: BigNumber
  needsApproval: boolean
  oraclePrice: BigNumber
  pairConfig?: IPairConfigDetails
  positionType: PositionType
  priceImpact: BigNumber
  quoteToken: Asset
  quoteTokenBalance: BigNumber
  tradeAmount: BigNumber
}

export default class NewPositionSetup implements INewPositionProps {
  balancesError: Error | null = null
  balancesPending = false
  baseToken = Asset.WBTC
  baseTokenBalance = new BigNumber(0)
  baseTokenPrice = new BigNumber(NaN)
  depositToken: Asset = Asset.WBTC
  dex: DEX = DEX.SUSHI
  entryPrice = new BigNumber(NaN)
  exposureValue = new BigNumber(NaN)
  inputTradeAmount = ''
  interestRate = new BigNumber(NaN)
  isExpired = false // TODO
  isLoading = false
  leverage = 0
  liquidationPrice = new BigNumber(NaN)
  loanDataBytes = '0x'
  maintenanceMargin = new BigNumber(NaN)
  marketLiquidity = new BigNumber(0)
  marketPair = { baseToken: Asset.WBTC, quoteToken: Asset.USDT }
  maxLeverage = 5
  maxTradeValue = new BigNumber(0)
  maxTradeValuePercent = 0
  minInitialMargin = new BigNumber(NaN)
  needsApproval = false
  oraclePrice = new BigNumber(NaN)
  /** Config and metadata for the current selected market */
  pairConfig?: IPairConfigDetails
  positionType = PositionType.LONG
  priceImpact = new BigNumber(NaN)
  quoteToken = Asset.USDT
  quoteTokenBalance = new BigNumber(0)
  rootStore: RootStore
  syncDataDebounced: DebouncedFunc<() => Promise<void>>
  tradeAmount = new BigNumber(0)
  tradeStore: TradeStore

  /** Help to prevent sending new position tx immediately one after another */
  get hasTxPending() {
    const txStatuses: TransactionStatus[] = ['sent', 'gas-check']
    const txTypes: TransactionType[] = ['trade-open-long', 'trade-open-short']
    return this.rootStore.transactionStore.transactions.some(
      (tx) => txStatuses.includes(tx.status) && txTypes.includes(tx.type)
    )
  }

  get isLong() {
    return this.positionType === PositionType.LONG
  }

  /**
   * 1x leverage is not allowed for Long
   */
  get minLeverage() {
    return this.isLong ? 1.1 : 1
  }

  /** Current setup mapped to a TradeRequest */
  get tradeRequest(): ITradeRequest {
    return {
      amount: this.tradeAmount,
      baseToken: this.baseToken,
      depositToken: this.depositToken,
      dex: this.dex,
      leverage: this.leverage,
      loanDataBytes: this.loanDataBytes,
      positionType: this.positionType,
      quoteToken: this.quoteToken,
      returnTokenIsCollateral: false, // mandatory because of ITradeRequest interface but not needed to open position
      tradeType: TradeType.OPEN_POSITION,
      positionValue: this.exposureValue.toNumber(),
    }
  }

  get depositTokenBalance() {
    return this.depositToken === this.baseToken ? this.baseTokenBalance : this.quoteTokenBalance
  }

  get loanToken() {
    return this.isLong ? this.quoteToken : this.baseToken
  }

  get issue(): NewPositionIssue | undefined {
    if (!this.inputTradeAmount) {
      return
    }
    const inputAmount = new BigNumber(this.inputTradeAmount)

    if (!this.rootStore.web3Connection.isConnected) {
      return 'NOT_CONNECTED'
    }

    if (inputAmount.isNaN() || this.tradeAmount.lt(0)) {
      return 'INVALID_TRADE_AMOUNT'
    }

    const lowLiquidity = this.marketLiquidity.lt(inputAmount)

    return this.depositTokenBalance && this.depositTokenBalance.isZero()
      ? 'ZERO_BALANCE'
      : lowLiquidity
      ? 'LOW_LIQUIDITY'
      : this.tradeAmount.gt(this.maxTradeValue)
      ? 'NOT_ENOUGH_FUNDS'
      : undefined
  }

  get loanIToken() {
    const { chainId } = this.rootStore.web3Connection
    if (!chainId) {
      return
    }
    const info = assetUtils.getItokenInfo(chainId, this.loanToken)
    const explorerAddress = getBlockExplorerAddressLink(chainId, info.address)

    return { ...info, explorerAddress }
  }

  get isDisabled() {
    // people should be able to close but not open KNCL
    return this.baseToken === Asset.KNCL
  }

  clearError() {
    this.balancesError = null
  }

  resetData(props: Partial<INewPositionProps> = {}) {
    this.assign({
      entryPrice: new BigNumber(NaN),
      exposureValue: new BigNumber(NaN),
      inputTradeAmount: '',
      interestRate: new BigNumber(NaN),
      liquidationPrice: new BigNumber(NaN),
      loanDataBytes: '0x',
      // maintenanceMargin: new BigNumber(NaN), need to better handle this
      marketLiquidity: new BigNumber(NaN),
      maxTradeValue: new BigNumber(0),
      maxTradeValuePercent: 0,
      minInitialMargin: new BigNumber(NaN),
      needsApproval: false,
      // oraclePrice: new BigNumber(NaN), need to better handle this
      priceImpact: new BigNumber(NaN),
      quoteTokenBalance: new BigNumber(0),
      tradeAmount: new BigNumber(0),
      ...props,
    })
  }

  setLeverage(value: number) {
    const leverage =
      value < this.minLeverage
        ? this.minLeverage
        : value > this.maxLeverage
        ? this.maxLeverage
        : numberUtils.round(value, 1)
    if (leverage !== this.leverage) {
      this.set('leverage', leverage)
      this.syncDataDebounced()
    }
  }

  /**
   * Helper to set values through mobx actions.
   */
  set(prop: keyof INewPositionProps, value: any) {
    ;(this[prop] as any) = value
  }

  async setLongShort(positionType: PositionType) {
    if (this.positionType !== positionType) {
      this.resetData({ positionType })
      await this.getMaxTradeValue(this.depositToken)
      this.syncDataDebounced()
    }
  }

  setDex(dex: DEX) {
    this.dex = dex
    this.syncDataDebounced()
  }

  setDepositToken(asset: Asset) {
    this.depositToken = asset
    this.getMaxTradeValue(asset)
    this.assign({ inputTradeAmount: '', tradeAmount: new BigNumber(0), maxTradeValuePercent: 0 })
  }

  setPair(marketPair: { baseToken: Asset; quoteToken: Asset }) {
    const { chainId } = this.rootStore.web3Connection
    if (!chainId || !isChainSupported(chainId)) {
      return
    }
    const pairConfig = getPairConfig(marketPair, chainId)
    const { depositTokens, initialPositionType: positionType } = pairConfig
    const depositToken = depositTokens[0]
    this.resetData()
    this.assign({
      ...marketPair,
      pairConfig,
      marketPair,
      depositToken,
      leverage: pairConfig.leverageInitial,
      positionType,
      maxLeverage: pairConfig.leverageMax,
      dex: pairConfig.dexes[0],
    })
    this.getMaxTradeValue(depositToken)
    this.getOraclePrice()
    this.getLoanParams()
  }

  refreshForChain(chainId: ChainId) {
    const pair = this.rootStore.settings.activePairs[chainId]
    this.setPair(pair)
  }

  async checkApproval() {
    const { chainId } = this.rootStore.web3Connection
    if (!chainId) {
      return
    }
    if (assetUtils.isNativeTokenForNetwork(chainId, this.depositToken)) {
      this.assign({ needsApproval: false })
      return
    }
    this.assign({ isLoading: true })
    const { spendingApprovals } = this.rootStore
    const spender = assetUtils.getITokenAddress(chainId, this.loanToken)
    const approval = await spendingApprovals.checkApproval(this.depositToken, spender)
    const needsApproval = this.depositTokenBalance.gt(approval.amount)
    this.assign({ needsApproval, isLoading: false })
    return needsApproval
  }

  async approveSpending() {
    if (!this.loanIToken) {
      return
    }
    this.assign({ isLoading: true })
    const success = await this.rootStore.spendingApprovals.setInfiniteApprovalForToken(
      this.depositToken,
      this.loanIToken.address
    )
    if (success) {
      this.assign({ needsApproval: false })
    }
    this.assign({ isLoading: false })
  }

  /**
   * Updates data such as exposure value, interest rate, price impact based on user input
   */
  async syncData() {
    const { fulcrumProvider } = this.rootStore
    const { chainId, isConnected } = this.rootStore.web3Connection
    if (!chainId || !isConnected) {
      return
    }
    const { tradeRequest } = this
    const { amount, leverage } = tradeRequest

    if (amount.isNaN() || amount.isZero()) {
      return
    }
    this.set('isLoading', true)
    const [loanDataBytes] = await this.getSwapData(tradeRequest)
    const { exposureValue, interestRate } = await tradeUtils.getEstimatedMarginDetails(
      fulcrumProvider,
      tradeRequest,
      { loanDataBytes }
    )
    this.assign({ exposureValue, interestRate })

    const { src, dest } = tradeUtils.getTradeTokens(chainId, tradeRequest)

    const collateralToPrincipalRate = await oracleApi.getSwapRate(
      fulcrumProvider,
      src.asset,
      dest.asset
    )

    const srcAmount =
      this.depositToken === src.asset
        ? amount.times(10 ** src.decimals)
        : amount
            .times(collateralToPrincipalRate.pow(-1))
            .times(10 ** src.decimals)
            .integerValue()

    const actualSwapRate = await swapRateUtils.getActualSwapRate(fulcrumProvider, {
      srcToken: src.asset,
      destToken: dest.asset,
      leverage,
      srcAmount,
      loanDataBytes,
      isAmountIn: true,
    })

    const priceImpact = await swapRateUtils.getTradeSlippageRate(fulcrumProvider, {
      srcToken: src.asset,
      destToken: dest.asset,
      amount: srcAmount.times(leverage),
      swapPriceTradeAmount: actualSwapRate.rate,
      loanDataBytes,
      isAmountIn: true,
    })

    const entryPrice = this.isLong ? actualSwapRate.rate.pow(-1) : actualSwapRate.rate

    this.assign({ entryPrice, priceImpact })
    this.getLiquidationPrice(tradeRequest, actualSwapRate.rate.pow(-1))
    this.set('isLoading', false)
  }

  setAmountByPercentage(maxTradeValuePercent: number) {
    const tradeAmount =
      maxTradeValuePercent === 100
        ? this.maxTradeValue
        : new BigNumber(formatPrecision(this.maxTradeValue.times(maxTradeValuePercent).div(100)))
    this.assign({ maxTradeValuePercent, tradeAmount, inputTradeAmount: tradeAmount.toFixed() })

    this.syncDataDebounced()
  }

  /** We let the user types whatever he wants but won't trigger anything if the input is invalid */
  setTradeAmount(input: string) {
    this.inputTradeAmount = input
    this.tradeAmount = new BigNumber(input)
    if (this.tradeAmount.isNaN()) {
      return
    }
    if (this.tradeAmount.gt(0)) {
      this.maxTradeValuePercent = this.tradeAmount
        .div(this.maxTradeValue)
        .times(100)
        .integerValue()
        .toNumber()
      this.syncDataDebounced()
    }
  }

  setTradeAmountToMax() {
    this.setAmountByPercentage(100)
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  assign(props: Partial<INewPositionProps>) {
    Object.assign(this, props)
  }

  async getOraclePrice(): Promise<BigNumber> {
    const { baseToken, quoteToken } = this
    const oraclePrice = await oracleApi.getSwapRate(
      this.rootStore.fulcrumProvider,
      baseToken,
      quoteToken
    )
    this.assign({ oraclePrice })
    return oraclePrice
  }

  async getLoanParams() {
    const { maintenanceMargin, minInitialMargin } = await loanUtils.getLoanParams(
      this.rootStore.fulcrumProvider,
      this.baseToken,
      this.quoteToken,
      false
    )
    this.assign({ maintenanceMargin, minInitialMargin })
  }

  async getLiquidationPrice(tradeRequest: ITradeRequest, collateralToPrincipalRate?: BigNumber) {
    const { baseToken, quoteToken, maintenanceMargin } = this
    const fp = this.rootStore.fulcrumProvider

    if (typeof collateralToPrincipalRate === 'undefined') {
      const srcToken = this.isLong ? baseToken : quoteToken
      const destToken = this.isLong ? quoteToken : baseToken
      collateralToPrincipalRate = await oracleApi.getSwapRate(fp, srcToken, destToken)
    }

    const liquidationPrice = await tradeUtils.getLiquidationPrice(
      fp,
      tradeRequest,
      collateralToPrincipalRate,
      { maintenanceMargin }
    )

    this.assign({ liquidationPrice })
  }

  async openPosition() {
    this.assign({ isLoading: true })
    const { tradeRequest } = this
    let dexSelectorFailed = false

    try {
      await this.getSwapData(tradeRequest)
    } catch (err) {
      err.title = 'No route found for this trade'
      dexSelectorFailed = true
      this.rootStore.setError(err)
    }

    this.assign({ isLoading: false })

    if (dexSelectorFailed) {
      return
    }

    return this.rootStore.tradeStore.trade(tradeRequest)
  }

  /** Wallet balances and liquidity available */
  async getMaxTradeValue(depositToken: Asset) {
    const { fulcrumProvider } = this.rootStore
    try {
      this.assign({ balancesError: null, balancesPending: true })
      const { maxTradeValue, marketLiquidity, ...rest } = await asyncRetry(
        () =>
          tradeUtils.getMaxTradeValueToOpenPosition(fulcrumProvider, {
            baseToken: this.baseToken,
            quoteToken: this.quoteToken,
            positionType: this.positionType,
            depositToken,
          }),
        2,
        2000
      )
      const update: Partial<INewPositionProps> = { maxTradeValue, marketLiquidity }
      const balance = depositToken === this.baseToken ? 'baseTokenBalance' : 'quoteTokenBalance'
      update[balance] = rest.depositTokenBalance
      this.assign(update)
      return update
    } catch (err) {
      errorUtils.decorateError(err, { title: 'Wallet balance & liquidity unavailable' })
      this.assign({ balancesError: err })
    } finally {
      this.assign({ balancesPending: false })
    }
  }

  /**
   * Updates loanDatabytes and returns swapData
   *
   * Note that trying to get a route with amount 0 will throw an error, so this function should not
   * be called if the tradeRequest amount is 0.
   */
  async getSwapData(tradeRequest: ITradeRequest): Promise<[string, BigNumber]> {
    const { fulcrumProvider } = this.rootStore
    let swapData: [string, BigNumber] = [this.loanDataBytes, new BigNumber(0)]
    try {
      swapData = await swapPayloadUtils.getSwapDataForNewPosition(fulcrumProvider, tradeRequest)
      this.assign({ loanDataBytes: swapData[0] })
    } catch (err) {
      err.title = 'Failed to get swapData'
      this.rootStore.setError(err)
    }
    return swapData
  }

  noop(arg: any) {
    console.log(arg)
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    this.tradeStore = rootStore.tradeStore
    this.syncDataDebounced = debounce(() => this.syncData(), 500)
    mobx.makeAutoObservable(
      this,
      { rootStore: false, tradeStore: false },
      { deep: false, autoBind: true }
    )
  }
}
